import React, { useEffect, useMemo, useState } from "react";
import AppLayout from "@/components/AppLayout";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Download, Plus, X } from "lucide-react";

const STATUS_LABEL = {
  draft: "Draft", approved: "Approved", on_hold: "On Hold",
  processing: "Processing", paid: "Paid", reversed: "Reversed",
  cancelled: "Cancelled", clawback: "Clawback",
};

const ACTIONS = [
  ["approve", "Approve"], ["hold", "Hold"], ["mark_processing", "Mark Processing"],
  ["mark_paid", "Mark Paid"], ["reverse", "Reverse"], ["cancel", "Cancel"],
  ["clawback", "Clawback"],
];

/**
 * Finance / Super Admin / Founder ledger. Create rows, transition status,
 * download PDF statements. Ops manager gets 403 from the API.
 */
export default function AdminPartnerEarnings() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [partnerFilter, setPartnerFilter] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [actionModal, setActionModal] = useState(null); // {row, action}

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter !== "all") params.status = statusFilter;
      if (partnerFilter.trim()) params.partner_code = partnerFilter.trim();
      const r = await api.get("/partner-earnings", { params });
      setRows(r.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load earnings");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter, partnerFilter]);

  const totals = useMemo(() => {
    const t = { gross: 0, net: 0 };
    rows.forEach(r => { t.gross += r.gross_amount || 0; t.net += r.net_payable || 0; });
    return t;
  }, [rows]);

  const downloadPdf = async (eid, code) => {
    try {
      const r = await api.get(`/partner-earnings/${eid}/statement.pdf`,
        { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `miro_payout_${code}_${eid.slice(0,8)}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "PDF download failed");
    }
  };

  const bulkPdf = async () => {
    if (!partnerFilter.trim()) {
      toast.error("Enter a partner code to download bulk statement");
      return;
    }
    try {
      const r = await api.get(`/partner-earnings/statement.pdf`,
        { params: { partner_code: partnerFilter.trim() }, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `miro_payout_${partnerFilter.trim()}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk PDF download failed");
    }
  };

  return (
    <AppLayout>
      <div className="p-6" data-testid="admin-partner-earnings">
        <div className="flex justify-between items-start mb-6">
          <div>
            <div className="miro-overline">Finance</div>
            <h1 className="font-display font-black text-3xl tracking-tight text-[var(--miro-navy)]">
              Partner Earnings Ledger
            </h1>
            <p className="text-sm text-[var(--miro-text-muted)] mt-1">
              Manual entries. No automatic commission calculation. Every state
              change requires a reason and is audited.
            </p>
          </div>
          <Button onClick={() => setShowCreate(true)} data-testid="new-earning-btn">
            <Plus size={14} className="mr-1"/> New entry
          </Button>
        </div>

        <div className="flex flex-wrap items-end gap-3 mb-4">
          <div>
            <div className="text-xs text-[var(--miro-text-muted)] mb-1">Status</div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-40" data-testid="filter-status"><SelectValue/></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                {Object.entries(STATUS_LABEL).map(([k, v]) => (
                  <SelectItem key={k} value={k}>{v}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <div className="text-xs text-[var(--miro-text-muted)] mb-1">Partner code</div>
            <Input value={partnerFilter} onChange={e => setPartnerFilter(e.target.value)}
                   placeholder="PRO-000001-2026"
                   className="w-56" data-testid="filter-partner-code"/>
          </div>
          <Button variant="outline" onClick={bulkPdf} data-testid="bulk-pdf-btn">
            <Download size={14} className="mr-1"/> Bulk statement PDF
          </Button>
        </div>

        <div className="bg-white border rounded-xl overflow-hidden">
          <div className="grid grid-cols-12 gap-2 px-4 py-2 text-[10px] uppercase tracking-widest text-[var(--miro-text-muted)] border-b bg-slate-50">
            <div className="col-span-2">Partner</div>
            <div className="col-span-2">Product</div>
            <div>Gross</div><div>TDS</div><div>Net</div>
            <div className="col-span-2">Status</div>
            <div className="col-span-3 text-right">Actions</div>
          </div>
          {loading && <div className="p-6 text-sm">Loading…</div>}
          {!loading && rows.length === 0 && (
            <div className="p-6 text-sm text-[var(--miro-text-muted)]">
              No entries yet. Click <b>New entry</b> to add one.
            </div>
          )}
          {rows.map(r => (
            <div key={r.id} className="grid grid-cols-12 gap-2 px-4 py-3 border-b text-sm items-center"
                 data-testid={`earning-row-${r.id}`}>
              <div className="col-span-2">
                <div className="font-mono text-xs">{r.partner_code}</div>
                <div className="text-xs text-[var(--miro-text-muted)] truncate">{r.partner_name}</div>
              </div>
              <div className="col-span-2 text-xs">{r.product}</div>
              <div>{Number(r.gross_amount || 0).toFixed(0)}</div>
              <div>{Number(r.tds || 0).toFixed(0)}</div>
              <div className="font-bold">{Number(r.net_payable || 0).toFixed(0)}</div>
              <div className="col-span-2">
                <span className="text-xs px-2 py-0.5 rounded bg-slate-100">
                  {STATUS_LABEL[r.status] || r.status}
                </span>
              </div>
              <div className="col-span-3 flex justify-end gap-1 flex-wrap">
                {ACTIONS.map(([a, label]) => (
                  <Button key={a} size="sm" variant="outline"
                    className="text-[10px] h-6 px-2"
                    onClick={() => setActionModal({ row: r, action: a })}
                    data-testid={`act-${a}-${r.id}`}>
                    {label}
                  </Button>
                ))}
                <Button size="sm" variant="ghost" className="text-[10px] h-6 px-2"
                  onClick={() => downloadPdf(r.id, r.partner_code)}
                  data-testid={`pdf-${r.id}`}>
                  <Download size={12}/>
                </Button>
              </div>
            </div>
          ))}
          {rows.length > 0 && (
            <div className="grid grid-cols-12 gap-2 px-4 py-2 bg-slate-50 text-sm font-bold">
              <div className="col-span-2">TOTALS</div>
              <div className="col-span-2"/>
              <div>{totals.gross.toFixed(0)}</div>
              <div/>
              <div>{totals.net.toFixed(0)}</div>
            </div>
          )}
        </div>
      </div>

      {showCreate && (
        <CreateEarningModal onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); load(); }} />
      )}
      {actionModal && (
        <ActionModal modal={actionModal}
          onClose={() => setActionModal(null)}
          onDone={() => { setActionModal(null); load(); }} />
      )}
    </AppLayout>
  );
}


function CreateEarningModal({ onClose, onCreated }) {
  const [f, setF] = useState({
    partner_code: "", product: "", client_ref: "",
    gross_amount: 0, deductions: 0, tds: 0, clawback_amount: 0,
    notes_internal: "", notes_partner: "",
  });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!f.partner_code || !f.product) {
      toast.error("Partner code + product are required"); return;
    }
    setBusy(true);
    try {
      await api.post("/partner-earnings", {
        ...f,
        gross_amount: Number(f.gross_amount || 0),
        deductions: Number(f.deductions || 0),
        tds: Number(f.tds || 0),
        clawback_amount: Number(f.clawback_amount || 0),
      });
      toast.success("Earning entry created (draft)");
      onCreated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Create failed");
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
         data-testid="create-earning-modal">
      <div className="bg-white rounded-xl w-full max-w-2xl p-6 relative">
        <button className="absolute top-3 right-3" onClick={onClose}
                data-testid="close-create"><X size={18}/></button>
        <h2 className="font-display font-black text-xl mb-4">New earning entry</h2>
        <div className="grid grid-cols-2 gap-3">
          <div><label className="text-xs">Partner code *</label>
            <Input value={f.partner_code} onChange={e => setF({ ...f, partner_code: e.target.value })}
              placeholder="PRO-000001-2026" data-testid="create-partner-code"/></div>
          <div><label className="text-xs">Product *</label>
            <Input value={f.product} onChange={e => setF({ ...f, product: e.target.value })}
              placeholder="wealth / study_abroad / mba" data-testid="create-product"/></div>
          <div><label className="text-xs">Client reference</label>
            <Input value={f.client_ref} onChange={e => setF({ ...f, client_ref: e.target.value })}
              placeholder="Internal client id" data-testid="create-client-ref"/></div>
          <div><label className="text-xs">Gross amount</label>
            <Input type="number" value={f.gross_amount}
              onChange={e => setF({ ...f, gross_amount: e.target.value })}
              data-testid="create-gross"/></div>
          <div><label className="text-xs">Deductions</label>
            <Input type="number" value={f.deductions}
              onChange={e => setF({ ...f, deductions: e.target.value })}
              data-testid="create-deductions"/></div>
          <div><label className="text-xs">TDS</label>
            <Input type="number" value={f.tds}
              onChange={e => setF({ ...f, tds: e.target.value })}
              data-testid="create-tds"/></div>
          <div><label className="text-xs">Clawback</label>
            <Input type="number" value={f.clawback_amount}
              onChange={e => setF({ ...f, clawback_amount: e.target.value })}
              data-testid="create-clawback"/></div>
          <div className="col-span-2"><label className="text-xs">Notes (internal, finance-only)</label>
            <Textarea rows={2} value={f.notes_internal}
              onChange={e => setF({ ...f, notes_internal: e.target.value })}
              data-testid="create-notes-internal"/></div>
          <div className="col-span-2"><label className="text-xs">Notes (visible to partner)</label>
            <Textarea rows={2} value={f.notes_partner}
              onChange={e => setF({ ...f, notes_partner: e.target.value })}
              data-testid="create-notes-partner"/></div>
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy} data-testid="submit-earning">
            {busy ? "Saving…" : "Create draft"}
          </Button>
        </div>
      </div>
    </div>
  );
}


function ActionModal({ modal, onClose, onDone }) {
  const { row, action } = modal;
  const [reason, setReason] = useState("");
  const [payRef, setPayRef] = useState("");
  const [payMethod, setPayMethod] = useState("neft");
  const [payDate, setPayDate] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!reason.trim()) { toast.error("Reason is required"); return; }
    setBusy(true);
    try {
      const payload = { action, reason };
      if (action === "mark_paid") {
        if (!payRef) { toast.error("Payment reference is required"); setBusy(false); return; }
        payload.payment_reference = payRef;
        payload.payout_method = payMethod;
        if (payDate) payload.payout_date = payDate;
      }
      await api.post(`/partner-earnings/${row.id}/action`, payload);
      toast.success(`Applied ${action}`);
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action failed");
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
         data-testid="action-modal">
      <div className="bg-white rounded-xl w-full max-w-md p-6 relative">
        <button className="absolute top-3 right-3" onClick={onClose}><X size={18}/></button>
        <h2 className="font-display font-black text-lg mb-1 capitalize">
          {action.replace(/_/g, " ")}
        </h2>
        <div className="text-xs text-[var(--miro-text-muted)] mb-3">
          {row.partner_code} · {row.product} · Net {Number(row.net_payable || 0).toFixed(0)}
        </div>
        <label className="text-xs">Reason *</label>
        <Textarea rows={3} value={reason} onChange={e => setReason(e.target.value)}
          data-testid="action-reason"/>
        {action === "mark_paid" && (
          <div className="mt-3 grid grid-cols-2 gap-2">
            <div className="col-span-2"><label className="text-xs">Payment reference *</label>
              <Input value={payRef} onChange={e => setPayRef(e.target.value)}
                placeholder="PAY-2026-07-001" data-testid="action-pay-ref"/></div>
            <div><label className="text-xs">Method</label>
              <Select value={payMethod} onValueChange={setPayMethod}>
                <SelectTrigger data-testid="action-pay-method"><SelectValue/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="neft">NEFT</SelectItem>
                  <SelectItem value="imps">IMPS</SelectItem>
                  <SelectItem value="upi">UPI</SelectItem>
                  <SelectItem value="rtgs">RTGS</SelectItem>
                </SelectContent>
              </Select></div>
            <div><label className="text-xs">Payout date</label>
              <Input type="date" value={payDate} onChange={e => setPayDate(e.target.value)}
                data-testid="action-pay-date"/></div>
          </div>
        )}
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy} data-testid="action-submit">
            {busy ? "Applying…" : "Confirm"}
          </Button>
        </div>
      </div>
    </div>
  );
}
